/**
 * Local PIN verifier for offline unlock (Phase 8, ADR-009 §3).
 *
 * Lives in `@kortex/shared` (not `packages/pos-local`) because both the
 * client (Android WebView JS engine and a plain desktop browser, via
 * `packages/pos-local`) and the server (`apps/api`, to hash a PIN once at
 * set-time so the raw digits never touch disk anywhere) need the exact same
 * hash/verify code. It deliberately does NOT reuse
 * `apps/api/src/modules/auth/password.ts`'s `node:crypto` scrypt: that is a
 * Node-only API and does not exist in a browser or WebView JS engine, which
 * this module must also run in. Web Crypto's `SubtleCrypto` is the only
 * portable primitive available in all three environments, and it does not
 * implement scrypt or Argon2 - PBKDF2 is the strongest KDF it exposes.
 * 600,000 iterations of PBKDF2-SHA256 is OWASP's 2023 floor for that
 * primitive with no additional pepper.
 *
 * Stored as: pbkdf2$<iterations>$<salt base64url>$<hash base64url>
 * - a different, unmistakable shape from the server's own
 * `scrypt$N$r$p$<salt>$<hash>` password format, so the two are never
 * compared cross-format by accident.
 */
const ITERATIONS = 600_000;
const HASH_BITS = 256;
const PIN_PATTERN = /^\d{4,8}$/;
function toBase64Url(bytes) {
    let binary = '';
    for (const byte of bytes)
        binary += String.fromCharCode(byte);
    return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}
function fromBase64Url(value) {
    const normalised = value.replace(/-/g, '+').replace(/_/g, '/');
    const padded = normalised.padEnd(normalised.length + ((4 - (normalised.length % 4)) % 4), '=');
    const binary = atob(padded);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i += 1)
        bytes[i] = binary.charCodeAt(i);
    return bytes;
}
async function deriveBits(pin, salt, iterations) {
    const keyMaterial = await crypto.subtle.importKey('raw', new TextEncoder().encode(pin.normalize('NFKC')), 'PBKDF2', false, ['deriveBits']);
    const bits = await crypto.subtle.deriveBits({ name: 'PBKDF2', salt, iterations, hash: 'SHA-256' }, keyMaterial, HASH_BITS);
    return new Uint8Array(bits);
}
/** Throws with a message safe to surface directly in a "set your PIN" form. */
export function assertValidOfflinePin(pin) {
    if (!PIN_PATTERN.test(pin)) {
        throw new Error('PIN must be 4 to 8 digits');
    }
}
export async function hashOfflinePin(pin) {
    assertValidOfflinePin(pin);
    const salt = crypto.getRandomValues(new Uint8Array(16));
    const derived = await deriveBits(pin, salt, ITERATIONS);
    return ['pbkdf2', String(ITERATIONS), toBase64Url(salt), toBase64Url(derived)].join('$');
}
export async function verifyOfflinePin(pin, stored) {
    const parts = stored.split('$');
    if (parts.length !== 4 || parts[0] !== 'pbkdf2')
        return false;
    const [, iterationsRaw, saltRaw, hashRaw] = parts;
    const iterations = Number(iterationsRaw);
    if (!Number.isInteger(iterations) || iterations <= 0 || !saltRaw || !hashRaw)
        return false;
    let salt;
    let expected;
    try {
        salt = fromBase64Url(saltRaw);
        expected = fromBase64Url(hashRaw);
    }
    catch {
        return false;
    }
    const derived = await deriveBits(pin, salt, iterations);
    if (derived.length !== expected.length)
        return false;
    // Web Crypto has no timingSafeEqual - constant-time compare by hand.
    let diff = 0;
    for (let i = 0; i < derived.length; i += 1)
        diff |= (derived[i] ?? 0) ^ (expected[i] ?? 0);
    return diff === 0;
}
//# sourceMappingURL=offlinePin.js.map